import { useState } from 'react'
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material'
import { AttachFile, Close, Image as ImageIcon } from '@mui/icons-material'
import { PRIORITY_OPTIONS } from './issues-constants'
import { RedactedSensorField } from '../demo/RedactedSensorField'

const EMPTY_FORM = {
	title: '',
	description: '',
	sensor_id: '',
	priority: 'medium',
	files: [],
}

export function CreateIssueDialog({ open, onClose, onSubmit, sensors, loading }) {
	const [form, setForm] = useState(EMPTY_FORM)

	const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))

	const handleFileChange = (e) => {
		const files = Array.from(e.target.files || [])
		setForm((prev) => ({ ...prev, files: [...prev.files, ...files] }))
	}

	const removeFile = (index) => {
		setForm((prev) => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		if (!form.title.trim()) return
		await onSubmit(
			{
				title: form.title.trim(),
				description: form.description.trim() || null,
				sensor_id: form.sensor_id || null,
				priority: form.priority,
			},
			form.files
		)
		setForm(EMPTY_FORM)
	}

	const handleClose = () => {
		setForm(EMPTY_FORM)
		onClose()
	}

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Report an Issue</DialogTitle>
			<form onSubmit={handleSubmit}>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} pt={1}>
						<TextField
							label="Title"
							value={form.title}
							onChange={set('title')}
							required
							fullWidth
							placeholder="Brief description of the issue"
						/>
						<TextField
							label="Description"
							value={form.description}
							onChange={set('description')}
							multiline
							rows={4}
							fullWidth
							placeholder="Provide details about the issue, steps to reproduce, etc."
						/>
						<FormControl fullWidth>
							<InputLabel>Sensor (optional)</InputLabel>
							<Select value={form.sensor_id} label="Sensor (optional)" onChange={set('sensor_id')}>
								<MenuItem value="">None / General issue</MenuItem>
								{sensors.map((s) => (
									<MenuItem key={s.sensor_id} value={s.sensor_id}>
										<RedactedSensorField inline minWidth={160} minHeight={20}>
											<span>
												{s.sensor_id}
												{s.sensor_location ? ` (${s.sensor_location})` : ''}
											</span>
										</RedactedSensorField>
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl fullWidth>
							<InputLabel>Priority</InputLabel>
							<Select value={form.priority} label="Priority" onChange={set('priority')}>
								{PRIORITY_OPTIONS.map((p) => (
									<MenuItem key={p} value={p}>
										{p.charAt(0).toUpperCase() + p.slice(1)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Box>
							<Typography variant="subtitle2" gutterBottom>
								Attachments (optional)
							</Typography>
							<Button
								variant="outlined"
								component="label"
								startIcon={<AttachFile />}
								size="small"
							>
								Choose Files
								<input
									type="file"
									hidden
									multiple
									accept="image/*,.pdf,.csv,.txt,.xlsx,.svg"
									onChange={handleFileChange}
								/>
							</Button>
							{form.files.length > 0 && (
								<List dense>
									{form.files.map((file, i) => (
										<ListItem key={i} sx={{ py: 0 }}>
											<ListItemIcon sx={{ minWidth: 36 }}>
												<ImageIcon fontSize="small" />
											</ListItemIcon>
											<ListItemText
												primary={file.name}
												secondary={`${(file.size / 1024).toFixed(1)} KB`}
											/>
											<IconButton size="small" onClick={() => removeFile(i)}>
												<Close fontSize="small" />
											</IconButton>
										</ListItem>
									))}
								</List>
							)}
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>Cancel</Button>
					<Button type="submit" variant="contained" disabled={loading}>
						{loading ? 'Creating…' : 'Create Issue'}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	)
}
